/**
 * The one vocabulary for a climber's Hat/Pathway rating.
 *
 * Four spellings of the negative rating are in flight across the app —
 * `DISLIKE` (the V1 write path), `NOT_FOR_ME` (the AppSync enum and the
 * reporting pipeline) and `NOTFORME` (MSHatDisplay's internal value space).
 * Display code should normalize to a `SentimentKey` first; see
 * `normalizeSentiment`. Nothing here changes what gets persisted.
 */
export type SentimentKey = "LOVE" | "LIKE" | "NOT_FOR_ME" | "NOT_SURE";

/** Read-only displays: chips, badges, metric rows. Past tense — a rating already given. */
export const SENTIMENT_LABELS: Record<SentimentKey, string> = {
  LOVE: "Loved",
  LIKE: "Liked",
  NOT_FOR_ME: "Not for Me",
  NOT_SURE: "Not Sure Yet",
};

/**
 * Rating controls. An unpressed button reads as an action, so LOVE/LIKE stay
 * verbs; the other two are identical to their display labels.
 */
export const SENTIMENT_ACTION_LABELS: Record<SentimentKey, string> = {
  LOVE: "Love",
  LIKE: "Like",
  NOT_FOR_ME: "Not for Me",
  NOT_SURE: "Not Sure Yet",
};

/**
 * `glyph` is the icon fill — the same value the climber sees on the Hat's rating
 * buttons, so a coach and a climber are looking at the same colour.
 *
 * `text` is the label colour on a `tint` background. It darkens where the glyph
 * hue is too light to read at 14px (amber, cyan) and matches `glyph` where that
 * hue already reads (magenta, blue).
 *
 * `tint` is the resting chip/badge background — the flat hex the climber-side
 * glyphs use for their active state, rather than an alpha, so a chip looks the
 * same on white and on `background.neutral`.
 */
export const SENTIMENT_COLORS: Record<
  SentimentKey,
  { glyph: string; text: string; tint: string }
> = {
  LOVE: { glyph: "#EA1D76", text: "#EA1D76", tint: "#FFF1EE" },
  LIKE: { glyph: "#078DEE", text: "#078DEE", tint: "#EBF6FE" },
  NOT_FOR_ME: { glyph: "#FFAB00", text: "#B76E00", tint: "#FFF8EB" },
  NOT_SURE: { glyph: "#00B8D9", text: "#006C9C", tint: "#EBF9FC" },
};

/** Unrated colours, shared by every idle rating button. */
export const SENTIMENT_IDLE_COLORS = {
  glyph: "#A5B0BA",
  tint: "#F4F6F8",
};

/** Best-to-worst, the order the rating buttons and metric rows read in. */
export const SENTIMENT_ORDER: SentimentKey[] = [
  "LOVE",
  "LIKE",
  "NOT_FOR_ME",
  "NOT_SURE",
];
